import { useState, useEffect, useMemo } from "react";
import { useStock } from "../App";
import { db } from "../firebase";
import { collection, addDoc, doc, updateDoc, query, where, getDocs } from "firebase/firestore";

const statusStyle = {
  Completed: { background: "#dcfce7", color: "#15803d" },
  Unpaid:    { background: "#fee2e2", color: "#b91c1c" },
  Loss:      { background: "#fee2e2", color: "#991b1b", border: "1px dashed #fecaca" },
};

const extractPrice = (priceStr) => {
  if (!priceStr) return 0;
  return parseFloat(priceStr.toString().replace(/[^0-9.-]+/g, "")) || 0;
};

const toDateTime = (dateStr, timeStr) => {
  if (!dateStr) return null;
  const dt = new Date(`${dateStr} ${timeStr || "12:00:00 AM"}`);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

const isPaidFromUnpaid = (sale) => {
  if (sale.paidFromUnpaid === true) return true;
  if (sale.paidFromUnpaid === false) return false;
  if (!sale.paidDate) return false;
  if (sale.paidDate !== sale.date) return true;
  if (sale.paidTime && sale.time && sale.paidTime !== sale.time) return true;
  return false;
};

const Icons = {
  Share: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line></svg>,
  Filter: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
};

export default function SalesPage() {
  const { products, sales, customers, balances, addBalance } = useStock(); 
  
  const [selectedMonth, setSelectedMonth] = useState("All Time");
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  
  // 🔥 NEW: Updated Filters
  const filters = ["All", "Unpaid", "Alipay", "WeChat", "Loss"]; 
  
  const [showModal, setShowModal] = useState(false);
  const [receiveModal, setReceiveModal] = useState(null); 
  const [receiptModal, setReceiptModal] = useState(null); 
  const [loading, setLoading] = useState(false);

  const [customerName, setCustomerName] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("Unpaid"); 
  
  const [saleDate, setSaleDate] = useState("");

  const [cart, setCart] = useState([
    { id: Date.now(), productId: "", qty: 1, customRate: "" }
  ]);

  // --- Pagination States ---
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [filter, search, selectedMonth]);

  // --- Smart Filtering & Memoization ---
  const { availableMonths, processedSales } = useMemo(() => {
    // Basic sort by date & time
    const baseSales = sales.filter(s => s.status !== "Expense").sort((a, b) => {
      const timeA = new Date(`${a.date} ${a.time || "12:00:00 AM"}`);
      const timeB = new Date(`${b.date} ${b.time || "12:00:00 AM"}`);
      return timeB - timeA;
    });

    // Extract unique months
    const monthSet = new Set();
    baseSales.forEach(s => {
      const d = new Date(s.date);
      if (!isNaN(d)) {
        monthSet.add(d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }));
      }
    });
    const aMonths = Array.from(monthSet).sort((a, b) => new Date(b) - new Date(a)); // Newest first

    let result = baseSales;

    // 1. Month Filter
    if (selectedMonth !== "All Time") {
      result = result.filter(s => {
        const d = new Date(s.date);
        return !isNaN(d) && d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) === selectedMonth;
      });
    }

    // 2. 🔥 Wallet & Status Filter
    if (filter !== "All") {
      if (filter === "Unpaid" || filter === "Loss") {
        result = result.filter(s => s.status === filter);
      } else if (filter === "Alipay") {
        result = result.filter(s => s.wallet === "FFC Alipay");
      } else if (filter === "WeChat") {
        result = result.filter(s => s.wallet === "FFC Wechat");
      }
    }

    // 3. Search Filter
    const normalizedSearch = search.trim().toLowerCase();
    if (normalizedSearch) {
      result = result.filter(s => {
        const haystack = `${s.id} ${s.customer} ${s.itemsSummary} ${s.status} ${s.wallet}`.toLowerCase();
        return haystack.includes(normalizedSearch);
      });
    }

    return { availableMonths: aMonths, processedSales: result };
  }, [sales, selectedMonth, filter, search]);

  // --- Pagination Logic ---
  const totalPages = Math.ceil(processedSales.length / itemsPerPage) || 1;
  const validPage = Math.min(currentPage, totalPages);
  const indexOfLastItem = validPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentSales = processedSales.slice(indexOfFirstItem, indexOfLastItem);

  const filteredCustomers = customers.filter(c => c.name.toLowerCase().includes(customerName.toLowerCase()));

  // Dynamic Unpaid Total based on the currently viewed list
  const totalUnpaid = processedSales
    .filter(s => s.status === "Unpaid")
    .reduce((sum, s) => sum + (s.amountNum || 0), 0);

  const lastUnpaidPaid = processedSales
    .filter(s => s.status === "Completed" && isPaidFromUnpaid(s))
    .map(s => ({ ...s, _paidAt: toDateTime(s.paidDate || s.date, s.paidTime || s.time) }))
    .filter(s => s._paidAt)
    .sort((a, b) => b._paidAt - a._paidAt)[0];

  const lastUnpaidPaidDate = lastUnpaidPaid?.paidDate || lastUnpaidPaid?.date || null;
  const lastUnpaidPaidTime = lastUnpaidPaid?.paidTime || lastUnpaidPaid?.time || null;
  const lastUnpaidPaidMethod = lastUnpaidPaid?.wallet && lastUnpaidPaid.wallet !== "Pending" ? lastUnpaidPaid.wallet : "N/A";
  const lastUnpaidPaidAmountNum = lastUnpaidPaid?.amountNum ?? extractPrice(lastUnpaidPaid?.amount);
  const lastUnpaidPaidAmount = Number.isFinite(lastUnpaidPaidAmountNum)
    ? `¥${lastUnpaidPaidAmountNum.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "N/A";

  const openSaleModal = () => {
    const now = new Date();
    const localDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    setSaleDate(localDate); 
    setShowModal(true);
  };

  const addCartItem = () => setCart([...cart, { id: Date.now(), productId: "", qty: 1, customRate: "" }]);
  const removeCartItem = (id) => setCart(cart.filter(item => item.id !== id));
  
  const updateCartItem = (id, field, value) => {
    setCart(cart.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const calculateTotal = () => {
    return cart.reduce((sum, item) => {
      const product = products.find(p => p.id === item.productId);
      if (!product) return sum;
      const rate = item.customRate ? parseFloat(item.customRate) : extractPrice(product.price);
      return sum + (rate * (item.qty || 0));
    }, 0);
  };

  const handleAddSale = async (e) => {
    e.preventDefault();

    if (!customerName) return alert("Please enter a customer name.");
    
    const validItems = cart.filter(item => item.productId && item.qty > 0);
    if (validItems.length === 0) return alert("Please add at least one valid product.");

    setLoading(true);
    try {
      const finalSaleTime = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });

      let hasStockIssue = false;
      let totalSaleAmount = 0;
      const itemsDetails = [];

      for (const item of validItems) {
        const product = products.find(p => p.id === item.productId);
        if (product.stock < item.qty) {
          alert(`Not enough stock for ${product.name}! Only ${product.stock} left.`);
          hasStockIssue = true;
          break;
        }

        const rate = item.customRate ? parseFloat(item.customRate) : extractPrice(product.price);
        const itemTotal = rate * item.qty;
        totalSaleAmount += itemTotal;
        
        const productQuery = query(collection(db, "products"), where("id", "==", product.id));
        const querySnapshot = await getDocs(productQuery);
        
        if (!querySnapshot.empty) {
          const realFirebaseDocId = querySnapshot.docs[0].id;
          await updateDoc(doc(db, "products", realFirebaseDocId), { stock: product.stock - item.qty });
        }

        itemsDetails.push({ name: product.name, qty: item.qty, rate: rate, total: itemTotal, unitCost: product.unitCost || 0 });
      }

      if (hasStockIssue) {
        setLoading(false);
        return;
      }

      if (paymentMethod !== "Unpaid") {
        addBalance(paymentMethod, totalSaleAmount);
      }

      const newSale = {
        id: `#SL-${Math.floor(1000 + Math.random() * 9000)}`,
        customer: customerName,
        itemsSummary: itemsDetails.map(i => `${i.name} (x${i.qty})`).join(", "),
        receiptItems: itemsDetails,
        totalQty: validItems.reduce((s, i) => s + i.qty, 0),
        amount: `¥${totalSaleAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`,
        amountNum: totalSaleAmount, 
        status: paymentMethod === "Unpaid" ? "Unpaid" : "Completed",
        date: saleDate, 
        time: finalSaleTime, 
        paidDate: paymentMethod !== "Unpaid" ? saleDate : null,
        paidTime: paymentMethod !== "Unpaid" ? finalSaleTime : null, 
        wallet: paymentMethod === "Unpaid" ? "Pending" : paymentMethod,
        paidFromUnpaid: false,
        createdAt: new Date()
      };

      await addDoc(collection(db, "sales"), newSale);
      
      setShowModal(false);
      setCustomerName("");
      setPaymentMethod("Unpaid");
      setCart([{ id: Date.now(), productId: "", qty: 1, customRate: "" }]);

      setReceiptModal(newSale); 

    } catch (err) {
      alert("Error processing sale: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReceivePayment = async (e) => {
    e.preventDefault();
    const wallet = e.target.wallet.value;
    
    try {
      const now = new Date();
      const payDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const payTime = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
      
      addBalance(wallet, receiveModal.amountNum);
      
      let realSalesId = receiveModal.docId || null;

      if (!realSalesId) {
        const salesQuery = query(collection(db, "sales"), where("id", "==", receiveModal.id));
        const querySnapshot = await getDocs(salesQuery);
        if (!querySnapshot.empty) {
          realSalesId = querySnapshot.docs[0].id;
        }
      }

      if (!realSalesId) {
        throw new Error("Sales record not found in database.");
      }

      await updateDoc(doc(db, "sales", realSalesId), {
        status: "Completed",
        paidDate: payDate,
        paidTime: payTime,
        wallet: wallet,
        paidFromUnpaid: true
      });
      
      setReceiveModal(null);
    } catch (err) {
      alert("Payment failed: " + err.message);
    }
  };

  const handleShare = async () => {
    if (!receiptModal) return;

    let itemsText = receiptModal.receiptItems 
      ? receiptModal.receiptItems.map(i => `${i.name} (x${i.qty}) - ¥${i.total.toFixed(2)}`).join("\n")
      : receiptModal.itemsSummary;

    const shareText = `🧾 FRESH FARM CORNER\nReceipt No: ${receiptModal.id}\nDate: ${receiptModal.date} ${receiptModal.time}\nCustomer: ${receiptModal.customer}\nStatus: ${receiptModal.status}\n\n-- Items --\n${itemsText}\n\nGrand Total: ${receiptModal.amount}\nPayment via: ${receiptModal.wallet}\n\nThank you for shopping with us!`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `Receipt ${receiptModal.id}`,
          text: shareText
        });
      } catch (err) {
        console.error("Share failed", err);
      }
    } else {
      navigator.clipboard.writeText(shareText);
      alert("Receipt details copied to clipboard!");
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      
      <div className="flex-resp">
        {Object.entries(balances).map(([method, amount]) => (
          <div key={method} style={{ background: "#1e293b", color: "white", padding: "16px 20px", borderRadius: 14, flex: 1, display: "flex", justifyContent: "space-between", alignItems: "center", minWidth: 200 }}>
            <span style={{ fontSize: 13, color: "#94a3b8", fontWeight: 500 }}>{method}</span>
            <span style={{ fontSize: 18, fontWeight: 700, fontFamily: "system-ui, sans-serif" }}>¥{amount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
          </div>
        ))}
        
        <div style={{ background: "#450a0a", color: "white", padding: "16px 20px", borderRadius: 14, flex: 1, display: "flex", justifyContent: "space-between", alignItems: "center", border: "1px solid #7f1d1d", minWidth: 200 }}>
          <span style={{ fontSize: 13, color: "#fca5a5", fontWeight: 500 }}>Pending / Unpaid {selectedMonth !== "All Time" && `(${selectedMonth})`}</span>
          <span style={{ fontSize: 18, fontWeight: 700, fontFamily: "system-ui, sans-serif", color: "#f87171" }}>
            ¥{totalUnpaid.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </span>
        </div>

        <div style={{ background: "#0f172a", color: "white", padding: "16px 20px", borderRadius: 14, flex: 1, display: "flex", justifyContent: "space-between", alignItems: "center", border: "1px solid #1e293b", minWidth: 220, gap: 12 }}>
          <div>
            <div style={{ fontSize: 13, color: "#94a3b8", fontWeight: 600 }}>Last Unpaid Paid</div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{lastUnpaidPaidDate || "No payment yet"}</div>
            {lastUnpaidPaidTime && <div style={{ fontSize: 11, color: "#94a3b8" }}>{lastUnpaidPaidTime}</div>}
            {lastUnpaidPaid && <div style={{ fontSize: 12, color: "#e2e8f0", fontWeight: 600, marginTop: 2 }}>{lastUnpaidPaidAmount}</div>}
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 12, color: "#94a3b8", fontWeight: 600 }}>Method</div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>{lastUnpaidPaid ? lastUnpaidPaidMethod : "N/A"}</div>
          </div>
        </div>
      </div>

      <div style={{ background: "white", borderRadius: 14, border: "1px solid #e2e8f0", padding: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
          <div>
            <p style={{ fontSize: 15, fontWeight: 600, color: "#0f172a", margin: 0 }}>All Sales Records</p>
            <button 
              onClick={openSaleModal}
              style={{ background: "#10b981", color: "white", border: "none", padding: "8px 16px", borderRadius: 8, fontSize: 13, cursor: "pointer", fontWeight: 600, marginTop: 10 }}
            >
              + Record New Sale
            </button>
          </div>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
            
            {/* Smart Monthly Filter */}
            <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#f8fafc", padding: "6px 12px", borderRadius: 8, border: "1px solid #e2e8f0" }}>
              <span style={{ color: "#64748b" }}>{Icons.Filter}</span>
              <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} style={{ background: "transparent", border: "none", outline: "none", fontSize: 13, fontWeight: 600, color: "#0f172a", cursor: "pointer" }}>
                <option value="All Time">All Time</option>
                {availableMonths.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>

            {/* Search Bar */}
            <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "6px 10px" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search sales..."
                style={{ border: "none", outline: "none", background: "transparent", fontSize: 12, color: "#0f172a", width: 140, fontWeight: 500 }}
              />
            </div>

            {/* Wallet & Status Filters */}
            <div style={{ display: "flex", gap: 6 }}>
              {filters.map((f) => (
                <button key={f} onClick={() => setFilter(f)}
                  style={{ fontSize: 12, padding: "6px 14px", borderRadius: 8, border: "1px solid", borderColor: filter === f ? "#10b981" : "#e2e8f0", background: filter === f ? "#d1fae5" : "white", color: filter === f ? "#15803d" : "#64748b", cursor: "pointer", fontWeight: filter === f ? 600 : 500 }}>
                  {f}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="table-responsive">
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "750px" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                {["Sale ID", "Customer", "Products", "Amount", "Status", "Date & Time", "Action"].map((h) => (
                  <th key={h} style={{ textAlign: "left", fontSize: 11, color: "#64748b", fontWeight: 600, padding: "0 8px 12px", textTransform: "uppercase" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentSales.map((s, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #f8fafc" }}>
                  <td style={{ padding: "14px 8px", fontSize: 13, color: "#6366f1", fontWeight: 600 }}>{s.id}</td>
                  <td style={{ padding: "14px 8px", fontSize: 13, color: "#0f172a", fontWeight: 600 }}>{s.customer}</td>
                  <td style={{ padding: "14px 8px", fontSize: 12, color: "#475569", maxWidth: 150, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.itemsSummary}</td>
                   <td style={{ padding: "14px 8px", fontSize: 14, color: s.status === "Loss" ? "#b91c1c" : "#0f172a", fontWeight: 700 }}>{s.amount?.replace('$', '¥')}</td>
                  <td style={{ padding: "14px 8px" }}>
                    <span style={{ ...statusStyle[s.status], fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 20 }}>{s.status}</span>
                  </td>
                  <td style={{ padding: "14px 8px", fontSize: 12, color: "#64748b" }}>
                    <div>{s.date}</div>
                    <div style={{ fontSize: 10, color: "#94a3b8" }}>{s.time}</div>
                  </td>
                  <td style={{ padding: "14px 8px", display: "flex", gap: 6, alignItems: "center" }}>
                    {s.status === "Unpaid" && (
                      <button onClick={() => setReceiveModal(s)} style={{ background: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca", padding: "6px 12px", borderRadius: 6, fontSize: 11, cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}>
                        Receive Payment
                      </button>
                    )}
                    {s.status !== "Loss" && (
                      <button onClick={() => setReceiptModal(s)} style={{ background: "white", color: "#64748b", border: "1px solid #e2e8f0", padding: "6px 10px", borderRadius: 6, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }} title="Share Receipt">
                        {Icons.Share}
                      </button>
                    )}
                    {s.status === "Loss" && <span style={{ fontSize: 11, color: "#94a3b8" }}>N/A</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {processedSales.length === 0 && <p style={{ textAlign: "center", color: "#94a3b8", fontSize: 13, padding: "40px 0" }}>No sales records found.</p>}
        </div>

        {totalPages > 1 && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 0 0", borderTop: "1px solid #f1f5f9", marginTop: 10, flexWrap: "wrap", gap: 10 }}>
            <span style={{ fontSize: 13, color: "#64748b", fontWeight: 500 }}>
              Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, processedSales.length)} of {processedSales.length} entries
            </span>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <button 
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #e2e8f0", background: currentPage === 1 ? "#f8fafc" : "white", color: currentPage === 1 ? "#cbd5e1" : "#475569", cursor: currentPage === 1 ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 600, transition: "all 0.2s" }}
              >
                Prev
              </button>

              <div style={{ display: "flex", gap: 4 }}>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(num => (
                  <button 
                    key={num} 
                    onClick={() => setCurrentPage(num)}
                    style={{ 
                      width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 6, border: "1px solid", 
                      borderColor: currentPage === num ? "#10b981" : "#e2e8f0", 
                      background: currentPage === num ? "#10b981" : "white", 
                      color: currentPage === num ? "white" : "#475569", 
                      cursor: "pointer", fontSize: 13, fontWeight: 600, transition: "all 0.2s" 
                    }}
                  >
                    {num}
                  </button>
                ))}
              </div>

              <button 
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #e2e8f0", background: currentPage === totalPages ? "#f8fafc" : "white", color: currentPage === totalPages ? "#cbd5e1" : "#475569", cursor: currentPage === totalPages ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 600, transition: "all 0.2s" }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* --- ADD SALE MODAL --- */}
      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 20, overflowY: "auto" }}>
          <div style={{ background: "white", padding: 24, borderRadius: 16, width: "100%", maxWidth: 650, margin: "auto" }}>
            <h3 style={{ margin: "0 0 20px", color: "#0f172a", fontSize: 18 }}>Record New Sale</h3>
            <form onSubmit={handleAddSale} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 200px", position: "relative" }}>
                  <label style={{ fontSize: 12, color: "#64748b", fontWeight: 600, display: "block", marginBottom: 6 }}>Customer Name <span style={{color: "#ef4444"}}>*</span></label>
                  <input 
                    required placeholder="Type customer name..." 
                    value={customerName} 
                    onChange={e => { setCustomerName(e.target.value); setShowSuggestions(true); }}
                    onFocus={() => setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    style={{ width: "100%", padding: "12px", borderRadius: 8, border: "1px solid #cbd5e1", outline: "none", boxSizing: "border-box", fontSize: 14, background: "#f8fafc" }} 
                  />
                  {showSuggestions && customerName && filteredCustomers.length > 0 && (
                    <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "white", border: "1px solid #e2e8f0", borderRadius: 8, marginTop: 4, zIndex: 50, maxHeight: 150, overflowY: "auto", boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }}>
                      {filteredCustomers.map(c => (
                        <div key={c.id} onClick={() => { setCustomerName(c.name); setShowSuggestions(false); }} style={{ padding: "10px 12px", cursor: "pointer", fontSize: 13, borderBottom: "1px solid #f1f5f9" }} onMouseEnter={e => e.target.style.background="#f8fafc"} onMouseLeave={e => e.target.style.background="white"}>
                          <strong>{c.name}</strong> <span style={{color: "#94a3b8"}}>({c.phone})</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div style={{ flex: "1 1 120px" }}>
                  <label style={{ fontSize: 12, color: "#64748b", fontWeight: 600, display: "block", marginBottom: 6 }}>Date</label>
                  <input type="date" required value={saleDate} onChange={e => setSaleDate(e.target.value)} style={{ width: "100%", padding: "12px", borderRadius: 8, border: "1px solid #cbd5e1", outline: "none", boxSizing: "border-box", fontSize: 14, background: "white", color: "#0f172a" }} />
                </div>
              </div>

              <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 10 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>Products</span>
                  <button type="button" onClick={addCartItem} style={{ background: "#eef2ff", color: "#4f46e5", border: "none", padding: "6px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>+ Add Product</button>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {cart.map((item) => {
                    const productInfo = products.find(p => p.id === item.productId);
                    const defaultPrice = productInfo ? productInfo.price : "-";
                    return (
                      <div key={item.id} style={{ display: "flex", gap: 10, alignItems: "center", background: "#f8fafc", padding: 10, borderRadius: 8, flexWrap: "wrap" }}>
                        <div style={{ flex: "1 1 200px" }}>
                          <select required value={item.productId} onChange={e => updateCartItem(item.id, "productId", e.target.value)} style={{ width: "100%", padding: "10px", borderRadius: 6, border: "1px solid #cbd5e1", outline: "none", background: "white", fontSize: 13 }}>
                            <option value="" disabled>Select a product...</option>
                            {products.filter(p => p.stock > 0).map(p => (
                              <option key={p.id} value={p.id}>{p.name} ({p.stock} {p.unit})</option>
                            ))}
                          </select>
                        </div>
                        <div style={{ flex: "1 1 80px" }}>
                          <input type="number" step="any" required min="0.01" placeholder="Qty" value={item.qty} onChange={e => updateCartItem(item.id, "qty", parseFloat(e.target.value) || "")} style={{ width: "100%", padding: "10px", borderRadius: 6, border: "1px solid #cbd5e1", outline: "none", boxSizing: "border-box", fontSize: 13 }} />
                        </div>
                        <div style={{ flex: "1 1 120px" }}>
                          <input type="number" step="any" placeholder={`Offer (${defaultPrice})`} value={item.customRate} onChange={e => updateCartItem(item.id, "customRate", e.target.value)} style={{ width: "100%", padding: "10px", borderRadius: 6, border: "1px solid #cbd5e1", outline: "none", boxSizing: "border-box", fontSize: 13, background: item.customRate ? "#fffbeb" : "white" }} />
                        </div>
                        {cart.length > 1 && (
                          <button type="button" onClick={() => removeCartItem(item.id)} style={{ background: "#fee2e2", color: "#b91c1c", border: "none", width: 34, height: 34, borderRadius: 6, cursor: "pointer", fontWeight: 700 }}>X</button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div style={{ display: "flex", gap: 16, background: "#f1f5f9", padding: 16, borderRadius: 10, alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 200px" }}>
                  <label style={{ fontSize: 12, color: "#0f172a", fontWeight: 600, display: "block", marginBottom: 6 }}>Payment Status</label>
                  <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} style={{ width: "100%", padding: "12px", borderRadius: 8, border: "2px solid #cbd5e1", outline: "none", boxSizing: "border-box", background: paymentMethod === "Unpaid" ? "#fee2e2" : "white", color: paymentMethod === "Unpaid" ? "#b91c1c" : "#0f172a", fontWeight: 600 }}>
                    <option value="Unpaid">⚠️ Unpaid (Due)</option>
                    <option value="FFC Alipay">FFC Alipay</option>
                    <option value="FFC Wechat">FFC Wechat</option>
                  </select>
                </div>
                <div style={{ flex: "1 1 150px", textAlign: "right" }}>
                  <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 4px 0" }}>Total Amount</p>
                  <p style={{ fontSize: 26, fontWeight: 800, color: "#10b981", margin: 0, fontFamily: "system-ui, sans-serif" }}>
                    ¥{calculateTotal().toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </p>
                </div>
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 5 }}>
                <button type="button" onClick={() => setShowModal(false)} style={{ flex: 1, padding: "12px", borderRadius: 8, border: "1px solid #cbd5e1", background: "white", cursor: "pointer", fontWeight: 600, color: "#64748b", fontSize: 14 }}>Cancel</button>
                <button type="submit" disabled={loading} style={{ flex: 1, padding: "12px", borderRadius: 8, border: "none", background: "#10b981", color: "white", fontWeight: 600, cursor: "pointer", fontSize: 14 }}>{loading ? "Processing..." : "Complete Transaction"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- RECEIPT MODAL --- */}
      {receiptModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 16, backdropFilter: "blur(4px)" }}>
          <div style={{ background: "white", borderRadius: 16, width: "100%", maxWidth: 360, display: "flex", flexDirection: "column", maxHeight: "90vh", overflow: "hidden", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)" }}>
            
            <div style={{ padding: "24px 20px", background: "white", color: "black", fontFamily: "monospace", overflowY: "auto", flex: 1 }}>
              <div style={{ textAlign: "center", borderBottom: "1px dashed #ccc", paddingBottom: 12, marginBottom: 12 }}>
                <h2 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 800 }}>FRESH FARM CORNER</h2>
                <p style={{ margin: 0, fontSize: 11, color: "#666" }}>Retail Receipt</p>
              </div>
              
              <div style={{ fontSize: 12, marginBottom: 15, display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}><span><strong>Receipt No:</strong></span> <span>{receiptModal.id}</span></div>
                <div style={{ display: "flex", justifyContent: "space-between" }}><span><strong>Date:</strong></span> <span>{receiptModal.date} {receiptModal.time}</span></div>
                
                {receiptModal.status === "Completed" && receiptModal.paidTime && (
                  <div style={{ display: "flex", justifyContent: "space-between" }}><span><strong>Paid At:</strong></span> <span>{receiptModal.paidDate} {receiptModal.paidTime}</span></div>
                )}
                
                <div style={{ display: "flex", justifyContent: "space-between" }}><span><strong>Customer:</strong></span> <span style={{ textAlign: "right", wordBreak: "break-word", maxWidth: "60%" }}>{receiptModal.customer}</span></div>
                <div style={{ display: "flex", justifyContent: "space-between" }}><span><strong>Status:</strong></span> <span>{receiptModal.status}</span></div>
              </div>

              <table style={{ width: "100%", fontSize: 12, textAlign: "left", borderCollapse: "collapse", marginBottom: 15, tableLayout: "fixed" }}>
                <thead>
                  <tr style={{ borderBottom: "1px dashed #ccc" }}>
                    <th style={{ paddingBottom: 5, width: "50%" }}>Item</th>
                    <th style={{ paddingBottom: 5, textAlign: "center", width: "20%" }}>Qty</th>
                    <th style={{ paddingBottom: 5, textAlign: "right", width: "30%" }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {receiptModal.receiptItems ? receiptModal.receiptItems.map((item, idx) => (
                    <tr key={idx}>
                      <td style={{ paddingTop: 8, wordBreak: "break-word", paddingRight: 4 }}>{item.name} <br/><span style={{ fontSize: 10, color: "#666" }}>@¥{item.rate}</span></td>
                      <td style={{ paddingTop: 8, textAlign: "center", verticalAlign: "top" }}>{item.qty}</td>
                      <td style={{ paddingTop: 8, textAlign: "right", verticalAlign: "top" }}>¥{item.total.toFixed(2)}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="3" style={{ paddingTop: 8 }}>{receiptModal.itemsSummary}</td>
                    </tr>
                  )}
                </tbody>
              </table>

              <div style={{ borderTop: "1px dashed #ccc", paddingTop: 12, textAlign: "right", fontSize: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span><strong>Grand Total:</strong></span>
                  <span><strong>{receiptModal.amount?.replace('$', '¥')}</strong></span>
                </div>
                <p style={{ margin: "2px 0 0", fontSize: 11, color: "#666" }}>Payment: {receiptModal.wallet}</p>
              </div>
              
              <div style={{ textAlign: "center", marginTop: 24, fontSize: 11, color: "#666", paddingTop: 10, borderTop: "1px dashed #eee" }}>
                <p style={{ margin: "10px 0 2px 0" }}>Thank you for shopping with us!</p>
                <p style={{ margin: 0 }}>Please visit again.</p>
              </div>
            </div>

            <div style={{ padding: "12px 16px", background: "#f8fafc", borderTop: "1px solid #e2e8f0", display: "flex", gap: 10, flexShrink: 0 }}>
              <button onClick={() => setReceiptModal(null)} style={{ flex: 1, padding: "12px", borderRadius: 8, border: "1px solid #cbd5e1", background: "white", cursor: "pointer", fontWeight: 600, color: "#64748b", fontSize: 13 }}>Close</button>
              <button onClick={handleShare} style={{ flex: 2, padding: "12px", borderRadius: 8, border: "none", background: "#6366f1", color: "white", fontWeight: 600, cursor: "pointer", display: "flex", justifyContent: "center", alignItems: "center", gap: 8, fontSize: 13 }}>
                {Icons.Share} Share Receipt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- RECEIVE PAYMENT MODAL --- */}
      {receiveModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 20 }}>
          <div style={{ background: "white", padding: 24, borderRadius: 16, width: "100%", maxWidth: 400 }}>
            <h3 style={{ margin: "0 0 10px", color: "#0f172a", fontSize: 18 }}>Receive Payment</h3>
            <p style={{ fontSize: 13, color: "#64748b", marginBottom: 20, marginTop: 0 }}>Collecting pending payment for {receiveModal.customer}</p>
            
            <form onSubmit={handleReceivePayment} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ background: "#e0f2fe", padding: 14, borderRadius: 10, textAlign: "center" }}>
                <p style={{ fontSize: 12, color: "#0369a1", margin: "0 0 4px 0", fontWeight: 600 }}>Amount Due</p>
                <p style={{ fontSize: 28, color: "#0284c7", margin: 0, fontWeight: 800, fontFamily: "system-ui, sans-serif" }}>{receiveModal.amount}</p>
              </div>
              
              <div>
                <label style={{ fontSize: 12, color: "#0f172a", fontWeight: 600, display: "block", marginBottom: 6 }}>Receive to Wallet</label>
                <select name="wallet" required style={{ width: "100%", padding: "12px", borderRadius: 8, border: "1px solid #cbd5e1", outline: "none", boxSizing: "border-box", background: "white", fontWeight: 600 }}>
                  <option value="FFC Alipay">FFC Alipay</option>
                  <option value="FFC Wechat">FFC Wechat</option>
                </select>
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 5 }}>
                <button type="button" onClick={() => setReceiveModal(null)} style={{ flex: 1, padding: "12px", borderRadius: 8, border: "1px solid #cbd5e1", background: "white", cursor: "pointer", fontWeight: 600, color: "#64748b" }}>Cancel</button>
                <button type="submit" style={{ flex: 1, padding: "12px", borderRadius: 8, border: "none", background: "#0ea5e9", color: "white", fontWeight: 600, cursor: "pointer" }}>Mark as Paid</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}